
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useBreakpoint, useIsMobile } from '@/hooks/use-mobile';
import CTAButton from '../CTAButton';
import { Link } from 'react-router-dom';

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureSectionProps {
  index: number;
  title: string;
  description: string;
  imagePlaceholder: string;
  features: FeatureItem[];
  ctaText: string;
  id?: string; // Added id prop to identify which feature section is being rendered
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  index,
  title,
  description,
  imagePlaceholder,
  features,
  ctaText,
  id
}) => {
  const isMobile = useIsMobile();
  const { isTablet } = useBreakpoint();
  
  // Check which section is being rendered
  const isAIAssistants = id === "ai-assistants";
  const isFlowBuilder = id === "flow-builder";
  const isIntelliForms = id === "intelliforms";
  const isSpotlightPages = id === "spotlight-pages";
  
  console.log(`Rendering feature section with ID: ${id}, isAIAssistants: ${isAIAssistants}, isFlowBuilder: ${isFlowBuilder}, isIntelliForms: ${isIntelliForms}, isSpotlightPages: ${isSpotlightPages}`);
  
  return (
    <div className="flex flex-col gap-5 md:gap-12 animate-fade-in">
      <div className="text-center max-w-3xl mx-auto px-3 sm:px-0">
        <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-[#003a70] mb-1.5 md:mb-4">{title}</h2>
        <p className="text-xs md:text-sm lg:text-base text-gray-600">{description}</p>
      </div>
      
      <div className="w-full animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        {isIntelliForms ? (
          // Special container for IntelliForms with adjusted height to match embed dimensions
          <div className="max-w-4xl mx-auto rounded-xl shadow-lg overflow-hidden border border-gray-100 transform hover:scale-[1.02] transition-transform duration-500 relative z-10 mx-3 sm:mx-auto">
            <div 
              className="w-full h-full" 
              dangerouslySetInnerHTML={{ 
                __html: `<div style="position: relative; padding-bottom: calc(47.447916666666664% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/TCHEabx9Yr7xd2yXF2Ab?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="(copy) Crafting Intelligent Forms with AskSpot" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" ></iframe></div>`
              }}
            />
          </div>
        ) : (
          <div className="aspect-video max-w-4xl mx-auto rounded-xl shadow-lg overflow-hidden border border-gray-100 transform hover:scale-[1.02] transition-transform duration-500 relative z-10 mx-3 sm:mx-auto">
            {isAIAssistants ? (
              // AI Assistants Arcade embed
              <div 
                className="w-full h-full" 
                dangerouslySetInnerHTML={{ 
                  __html: `<div style="position: relative; padding-bottom: calc(56.27277459656429% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/NEA9zy7r69fJORliF46k?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Effortless Chatbot Customization and Integration" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" ></iframe></div>`
                }}
              />
            ) : isFlowBuilder ? (
              // Flow Builder Arcade embed
              <div 
                className="w-full h-full" 
                dangerouslySetInnerHTML={{ 
                  __html: `<div style="position: relative; padding-bottom: calc(56.22254758418741% + 41px); height: 0; width: 100%;"><iframe src="https://demo.arcade.software/N2chnWnl9gnZAGai5JRs?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" title="Flow Builder Demo" frameborder="0" loading="lazy" webkitallowfullscreen mozallowfullscreen allowfullscreen allow="clipboard-write" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; color-scheme: light;" ></iframe></div>`
                }}
              />
            ) : isSpotlightPages ? (
              // Spotlight Pages placeholder with preview mockup
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 w-full h-full flex items-center justify-center">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-20"></div>
                <div className="relative z-10 text-center p-8">
                  <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
                    <div className="w-12 h-12 bg-[#003a70] rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-[#003a70] mb-2">Spotlight Pages Demo</h3>
                    <p className="text-sm text-gray-600 mb-4">Interactive demo coming soon</p>
                    <div className="text-xs text-gray-500">Preview of AI-generated landing pages</div>
                  </div>
                </div>
              </div>
            ) : (
              // For all other sections, show the placeholder
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full p-2 md:p-4 relative">
                  <div className="absolute top-0 left-0 right-0 h-6 md:h-10 bg-[#003a70] rounded-t-lg flex items-center px-3 md:px-4">
                    <div className="flex space-x-1.5 md:space-x-2">
                      <div className="w-1.5 h-1.5 md:w-3 md:h-3 rounded-full bg-red-500"></div>
                      <div className="w-1.5 h-1.5 md:w-3 md:h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-1.5 h-1.5 md:w-3 md:h-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <div className="pt-8 md:pt-12 flex items-center justify-center h-full">
                    <span className="text-xs md:text-sm text-gray-400 font-medium">Product Screenshot Coming Soon</span>
                  </div>
                </div>
              </div>
            )}
            <img src={imagePlaceholder} alt={title} className="w-full h-full object-cover opacity-0" />
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 max-w-5xl mx-auto px-2 sm:px-0">
        {features.map((feature, idx) => (
          <div 
            key={idx} 
            className="bg-white rounded-xl p-2.5 md:p-6 shadow-md hover:shadow-lg transition-shadow animate-fade-in-up" 
            style={{ animationDelay: `${0.3 + idx * 0.1}s` }}
          >
            <div className="p-1.5 md:p-2.5 bg-accent rounded-lg mb-2 md:mb-4 inline-block shadow-sm">
              {React.isValidElement(feature.icon) ? 
                React.cloneElement(feature.icon as React.ReactElement, {
                  className: "text-[#003a70] h-4 w-4 md:h-5 md:w-5"
                }) : 
                <span className="text-[#003a70]">Icon</span>
              }
            </div>
            <h3 className="font-semibold text-[#003a70] text-sm md:text-base lg:text-lg mb-1 md:mb-2">{feature.title}</h3>
            <p className="text-[10px] md:text-xs lg:text-sm text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>
      
      <div className="flex justify-center mt-1 md:mt-4 px-3 sm:px-0 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
        <Link to="/pricing">
          <CTAButton variant="primary" className="group w-full sm:w-auto">
            <span className="truncate text-sm md:text-base">{ctaText}</span> 
            <ArrowRight className="inline-block ml-2 group-hover:translate-x-1 transition-transform" size={isMobile ? 14 : 18} />
          </CTAButton>
        </Link>
      </div>
    </div>
  );
};

export default FeatureSection;
